/**
 * @file Resolves the next distribution date to show on the schedule form.
 */
import dayjs from "dayjs";

import type { Frequency, ScheduleFormValues } from "../../types";

import getNextRecurrenceDate from "./getNextRecurrenceDate";

type Params = {
  scheduleId?: string;
  startDate: string | undefined;
  distributionFrequency: Frequency | undefined;
  initialValues: Pick<ScheduleFormValues, "startDate" | "nextDate">;
};

/**
 * Checks whether two ISO dates fall on the same calendar day.
 */
const isSameCalendarDay = (left: string, right: string): boolean => {
  const leftDay = dayjs(left).startOf("day");
  const rightDay = dayjs(right).startOf("day");

  return leftDay.isSame(rightDay);
};

/**
 * Computes the next recurrence date from a start date and frequency, when both are known.
 */
const computeCalculatedNextDate = (
  startDate: string | undefined,
  frequency: Frequency | undefined,
): string | undefined => {
  const hasMissingRecurrenceInput = !startDate || !frequency;

  if (hasMissingRecurrenceInput) {
    return undefined;
  }

  return getNextRecurrenceDate(startDate, frequency);
};

/**
 * Checks whether the form's start date still matches the schedule's saved start date.
 */
const isStartDateUnchangedFromInitial = (
  startDate: string | undefined,
  initialStartDate: string | undefined,
): boolean => {
  const hasMissingStartForComparison = !startDate || !initialStartDate;

  if (hasMissingStartForComparison) {
    return false;
  }

  return isSameCalendarDay(startDate, initialStartDate);
};

/**
 * Resolves the next distribution date, keeping the saved date when the start date has not changed.
 */
const getNextDate = ({
  scheduleId,
  startDate,
  distributionFrequency,
  initialValues,
}: Params): string | undefined => {
  const calculatedNextDate = computeCalculatedNextDate(
    startDate,
    distributionFrequency,
  );

  const isEditingExistingSchedule = Boolean(scheduleId);

  const isStartDateUnchanged = isStartDateUnchangedFromInitial(
    startDate,
    initialValues.startDate,
  );
  
  const shouldUseDocumentNextDate =
    isEditingExistingSchedule && isStartDateUnchanged;

  const documentNextDate = initialValues.nextDate;

  const resolvedNextDate = shouldUseDocumentNextDate
    ? (documentNextDate ?? calculatedNextDate)
    : calculatedNextDate;

  return resolvedNextDate;
};

export default getNextDate;
